import React, { useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { TextField, TextAreaField, SelectField } from "@/components/forms/Field";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const INITIAL = {
  name: "",
  company: "",
  role: "",
  email: "",
  phone: "",
  company_size: "",
  fix: "",
};

const SIZES = ["1 to 5", "6 to 20", "21 to 50", "51 to 200", "More than 200"];

const TEXT_FIELDS = [
  { id: "name", label: "Name", required: true, placeholder: "Your name" },
  { id: "company", label: "Company", required: true, placeholder: "Your company" },
  { id: "role", label: "Role", required: true, placeholder: "Founder, sales lead, marketing" },
  { id: "email", label: "Work email", type: "email", required: true, placeholder: "you@company.com" },
  { id: "phone", label: "Phone or WhatsApp", required: true, minLength: 5, placeholder: "+65 9123 4567" },
];

export default function AuditForm({ onDark = false }) {
  const [searchParams] = useSearchParams();
  const enquiryType = searchParams.get("enquiry") === "custom" ? "Custom" : "Audit";
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setSubmitting(true);
      setErrorMessage("");
      try {
        await axios.post(`${BACKEND_URL}/api/audit-request`, { ...form, enquiry_type: enquiryType });
        toast.success("Request received", { description: "We will come back to you within one working day." });
        setForm(INITIAL);
        setSent(true);
      } catch (err) {
        const raw = err?.response?.data?.detail;
        const detail = typeof raw === "string"
          ? raw
          : Array.isArray(raw)
            ? "Please check your details and try again."
            : "";
        setErrorMessage(detail || "Something went wrong. Please try again, or email us directly.");
        toast.error("Could not send that", { description: detail || "Please try again, or email us directly." });
      } finally {
        setSubmitting(false);
      }
    },
    [form, enquiryType]
  );

  return (
    <form
      onSubmit={handleSubmit}
      className={`${onDark ? "bg-[#F4E9D8]" : "bg-[#EADCC6]"} border border-[#1F1F1C]/10 rounded-2xl p-7 sm:p-9 space-y-5`}
      data-testid="audit-form"
    >
      {enquiryType === "Custom" && (
        <p className="text-sm text-[#4A463F]" data-testid="audit-form-custom-note">
          This goes to us tagged as a custom workflow enquiry.
        </p>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {TEXT_FIELDS.map((f) => (
          <TextField key={f.id} {...f} value={form[f.id]} onChange={handleChange} testId={`audit-form-${f.id}`} />
        ))}
        <SelectField
          id="company_size"
          label="Company size"
          required
          options={SIZES}
          placeholder="Select a size"
          value={form.company_size}
          onChange={handleChange}
          testId="audit-form-company-size"
        />
      </div>

      <TextAreaField
        id="fix"
        label="What would you most like to fix?"
        required
        rows={4}
        minLength={5}
        value={form.fix}
        onChange={handleChange}
        placeholder="Tell us what is slowing your sales process down today."
        testId="audit-form-fix"
      />

      <button
        type="submit"
        disabled={submitting}
        className="btn-press w-full bg-[#C8703A] text-[#F4E9D8] py-4 rounded-2xl font-semibold text-sm hover:bg-[#B35F2C] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        data-testid="audit-form-submit"
      >
        {submitting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> Sending
          </>
        ) : (
          <>
            {sent ? "Send another request" : "Send my request"} <ArrowRight className="w-4 h-4" strokeWidth={2.25} />
          </>
        )}
      </button>

      {sent && !errorMessage && (
        <p className="text-sm font-semibold text-[#3D472D]" data-testid="audit-form-success">
          Thanks, that is with us. We will come back to you within one working day.
        </p>
      )}
      {errorMessage && (
        <p className="text-sm font-semibold text-[#A13A1E]" data-testid="audit-form-error">
          {errorMessage}
        </p>
      )}

      <p className="text-xs text-[#4A463F] leading-relaxed" data-testid="audit-form-consent">
        By sending this, you agree to us contacting you about your audit, and to our{" "}
        <Link to="/privacy" className="font-semibold text-[#C8703A] underline underline-offset-2">Privacy Policy</Link> and{" "}
        <Link to="/terms" className="font-semibold text-[#C8703A] underline underline-offset-2">Terms</Link>.
      </p>
    </form>
  );
}
